import { humanFilesize, nearestMB, extractExtension } from '@/utils/files';

export function logAmplitudeEvent(eventName, eventContext = null) {
  if (!eventName) {
    throw Error('No event name given');
  }

  /**
   * If we're running in dev/qa/staging, we log the event to the console
   * so that it's easier to test/debug.
   */
  if (process.env.VUE_APP_ENV !== 'production') {
    if (eventContext) {
      console.group();
    }
    console.info('%cAmplitude event logged:', 'color: steelblue;', eventName);
    if (eventContext) {
      console.dir(eventContext);
      console.groupEnd();
    }
  }

  if (!window.amplitude) {
    return;
  }

  window.amplitude.getInstance().logEvent(eventName, eventContext);
}

/**
 * A helper utility to create a logger for amplitude where
 * we don't need to specify the full event name. It performs
 * some checking to ensure that we have a value for both
 * event category (aka data-ec) and event type (aka data-et),
 * which makes it useful for logging in `ui` and `library`
 * components where a prop for logging may be defined but
 * may be missing.
 *
 * usage:
 *
 *   const logEvent = createEventLogger('event_category')
 *
 *   // elsewhere in the code
 *   logEvent('event_type')
 *   logEvent('event_type_with_context', {
 *     variable1: 'var1',
 *     variable2: 'var2',
 *   })
 */
export function createEventLogger(category) {
  return (type, context = null) => {
    if (!category || !type) {
      return;
    }

    logAmplitudeEvent(`${category}--${type}`, context);
  };
}

export function logFileSizeForAmplitude(file) {
    if (!(file instanceof File)) {
      return;
    }

    if (file.size <= 0) {
        return;
    }

    /**
     * TODO: see if we can merge the two calls into one - need
     * to ensure that the properties are filterable separately
     * in amplitude (that is, that they aren't dependent on
     * each other)
     */
    logAmplitudeEvent('uploaded-filesize-range', {
        size: nearestMB(file.size),
    });
    logAmplitudeEvent('uploaded-filesize-human', {
        size: humanFilesize(file.size),
    });
}

export function logFileTypeForAmplitude(file) {
    if (!(file instanceof File)) {
      return;
    }

    const extension = extractExtension(file.name) || 'unknown';

    logAmplitudeEvent('uploaded-filetype', {
        type: extension.toLowerCase(),
    });
}

export default {
    logAmplitudeEvent,
    createEventLogger,
    logFileSizeForAmplitude,
    logFileTypeForAmplitude,
};
